import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';
import axios from 'axios';
import { FiSave, FiX, FiFileText, FiUpload, FiPlus, FiTrash2 } from 'react-icons/fi';
import AsyncSelect from 'react-select/async';
import * as XLSX from 'xlsx';
import withAuth from '../common/auth';
import {
  BASE_URL,
  GET_SUPPLIER_MASTER,
  POST_SUPPLIER_MAPPING,
  GET_ITEM_MASTER
} from '../utlilities/config';

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '34px',
    height: '34px',
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    height: '34px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '34px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
        ? '#92c3fcff'
        : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Define all purchase fields that need mapping
const PURCHASE_FIELDS = {
  // Header fields
  // Product fields
  product: [
    { key: 'productName', label: 'Product Name', required: false },
    { key: 'batch', label: 'Batch No', required: false },
    { key: 'expiry', label: 'Expiry', required: false },
    { key: 'packing', label: 'Packing', required: false },
    { key: 'qty', label: 'Quantity', required: false },
    { key: 'foc', label: 'Free (FOC)', required: false },
    { key: 'rate', label: 'Rate', required: false },
    { key: 'mrp', label: 'MRP', required: false },
    { key: 'discount', label: 'Discount', required: false },
    { key: 'discount%', label: 'Discount %', required: false },
    { key: 'hsnCode', label: 'HSN Code', required: false },
    { key: 'gstRate', label: 'GST %', required: false },
    { key: 'cgst', label: 'CGST %', required: false },
    { key: 'sgst', label: 'SGST %', required: false },
  ],
};

// Normalize product data helper
const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
    };
  });

const SupplierMapping = () => {
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [mappings, setMappings] = useState({});
  const [productMappings, setProductMappings] = useState([]);
  // Store selected product options to handle dynamically loaded products
  const [selectedProductOptions, setSelectedProductOptions] = useState({});
  const [productsMaster, setProductsMaster] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingMapping, setExistingMapping] = useState(null);
  const [unmatchedProducts, setUnmatchedProducts] = useState([]);
  const fileInputRef = useRef(null);
  const productSelectRefs = useRef([]);

  const token = localStorage.getItem('accessToken');
  const productSearchDebounceRef = useRef(null);

  const defaultHeaders = useMemo(() => {
    const headers = {
      'XApiKey': process.env.REACT_APP_API_KEY,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [token]);

  // Initialize mappings structure
  const initializeMappings = () => {
    const initialMappings = {};

    // Initialize product fields
    PURCHASE_FIELDS.product.forEach(field => {
      initialMappings[field.key] = '';
    });

    return initialMappings;
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          ...defaultHeaders,
          FilterText: '',
        },
      });

      if (response.status === 200) {
        setSuppliers(response.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Could not load suppliers', {
        autoClose: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          ...defaultHeaders,
          'PageNo': 1,
          'PageSize': 1000000,
          'FilterText': '',
          'FilterColumn': '0',
          'FilterValue': '0',
        },
      });

      if (response.status === 200) {
        const masterData = response.data?.data?.masterData || [];
        const pharmacyItems = masterData.filter(item => (item.ItemType ?? item.itemType) === 'Pharmacy');
        const normalizedProducts = normalizeProductData(pharmacyItems);
        setProductsMaster(normalizedProducts);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Could not load products', {
        autoClose: 1500,
      });
    }
  };

  // Load product options for AsyncSelect
  const loadProductOptions = useCallback((inputValue, callback) => {
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }

    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }

    productSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            ...defaultHeaders,
            'PageNo': 1,
            'PageSize': 1000000,
            'FilterText': inputValue,
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(item => (item.ItemType ?? item.itemType) === 'Pharmacy');
          const normalizedProducts = normalizeProductData(pharmacyItems);

          const options = normalizedProducts.map((product) => ({
            value: product.id,
            label: product.Item_Name ?? product.ItemName ?? '',
            ...product,
          }));

          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        callback([]);
      }
    }, 300);
  }, [defaultHeaders]);

  // Get selected product option for AsyncSelect
  const getSelectedProductOption = useCallback((productId, storedOption) => {
    if (!productId) return null;
    
    // First, try to use the stored option if available (for dynamically loaded products)
    if (storedOption && storedOption.value === productId) {
      return storedOption;
    }
    
    // Fallback to productsMaster
    const product = productsMaster.find(p => p.id === productId);
    if (!product) return null;
    return {
      value: product.id,
      label: product.Item_Name ?? product.ItemName ?? '',
      ...product,
    };
  }, [productsMaster]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    setMappings(initializeMappings());

    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, []);

  // Load existing mapping from supplier data
  const loadExistingMapping = (SupplierID) => {
    // Find the supplier in the already fetched suppliers array
    // Convert to number for comparison to handle string/number mismatches
    const supplierIdNum = Number(SupplierID);
    const supplier = suppliers.find(s => {
      const sId = Number(s.SupplierID || s.id || 0);
      return sId === supplierIdNum;
    });

    // Handle both Mappings (capital M) and mappings (lowercase) from API
    const existingMappingsData = supplier?.Mappings || supplier?.mappings || {};

    // Extract columns and products from mappings object
    // Support both old structure (direct mappings) and new structure (mappings.columns, mappings.products)
    const existingColumns = existingMappingsData.columns || existingMappingsData;
    const existingProductsData = existingMappingsData.products || supplier?.Products || {};

    // Convert Products object to array format for internal use
    // API returns Products as object: { "supplierProductName": "internalProductName", ... }
    // We need array format: [{ supplierProductName: "...", internalProductId: "...", internalProductName: "...", ... }]
    let existingProductMappings = [];
    const initialOptions = {};
    if (Array.isArray(existingProductsData)) {
      // Backward compatibility: if it's already an array
      existingProductMappings = existingProductsData;
      // Store options for existing mappings
      existingProductMappings.forEach((mapping, idx) => {
        if (mapping.internalProductId) {
          const product = productsMaster.find(p => p.id === mapping.internalProductId);
          if (product) {
            initialOptions[idx] = {
              value: product.id,
              label: product.Item_Name ?? product.ItemName ?? '',
              ...product,
            };
          }
        }
      });
    } else if (existingProductsData && typeof existingProductsData === 'object') {
      // Convert object to array
      existingProductMappings = Object.entries(existingProductsData).map(([supplierName, productName], idx) => {
        // Find product by name from productsMaster to get the ID
        const product = productsMaster.find(p => {
          const pName = p.Item_Name ?? p.ItemName ?? '';
          return pName.toLowerCase() === productName.toLowerCase();
        });
        const mapping = {
          supplierProductName: supplierName,
          internalProductId: product ? product.id : '',
          internalProductName: productName || (product ? (product.Item_Name ?? product.ItemName ?? '') : '')
        };
        // Store option if product found
        if (product) {
          initialOptions[idx] = {
            value: product.id,
            label: product.Item_Name ?? product.ItemName ?? '',
            ...product,
          };
        }
        return mapping;
      });
    }
    setSelectedProductOptions(initialOptions);

    // Check if there are any mappings (either columns or products)
    const hasColumns = existingColumns && typeof existingColumns === 'object' && Object.keys(existingColumns).length > 0;
    const hasProducts = existingProductMappings.length > 0;

    if (supplier && (hasColumns || hasProducts)) {
      // Merge existing column mappings with initialized structure to ensure all fields are present
      const mergedMappings = initializeMappings();

      // Copy existing column mappings (only if it's the columns object, not the full mappings)
      if (existingColumns && typeof existingColumns === 'object') {
        Object.keys(existingColumns).forEach(key => {
          if (mergedMappings.hasOwnProperty(key)) {
            mergedMappings[key] = existingColumns[key] || '';
          }
        });
      }

      setMappings(mergedMappings);
      setProductMappings(hasProducts ? existingProductMappings : []);
      setIsEditMode(true);
      setExistingMapping(supplier);
    } else {
      // No existing mappings, initialize empty
      setMappings(initializeMappings());
      // Start with one empty product mapping row for convenience
      setProductMappings([{ supplierProductName: '', internalProductId: '', internalProductName: '' }]);
      setSelectedProductOptions({});
      setIsEditMode(false);
      setExistingMapping(null);
    }
  };

  // Handle supplier selection
  const handleSupplierChange = (selectedOption) => {
    setSelectedSupplier(selectedOption);
    setUnmatchedProducts([]); // Clear unmatched products when supplier changes
    if (selectedOption) {
      const SupplierID = selectedOption.value;
      loadExistingMapping(SupplierID);
    } else {
      setMappings(initializeMappings());
      // Start with one empty product mapping row
      setProductMappings([{ supplierProductName: '', internalProductId: '', internalProductName: '' }]);
      setSelectedProductOptions({});
      setIsEditMode(false);
      setExistingMapping(null);
    }
  };

  // Handle mapping change
  const handleMappingChange = (fieldKey, value) => {
    setMappings(prev => ({
      ...prev,
      [fieldKey]: value.trim(),
    }));
  };

  // Validate mappings
  const validateMappings = () => {
    const errors = [];

    // Check required product fields
    PURCHASE_FIELDS.product.forEach(field => {
      if (field.required && !mappings[field.key]) {
        errors.push(`${field.label}`);
      }
    });

    return errors;
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier', { autoClose: 1500 });
      return;
    }

    const errors = validateMappings();
    if (errors.length > 0) {
      toast.error(`Please complete required mappings: ${errors.join(', ')}`, {
        autoClose: 2000
      });
      return;
    }

    setLoading(true);
    try {
      // Convert productMappings array to Products object
      // Format: { "supplierProductName": "internalProductName", ... }
      const filteredProducts = productMappings.filter(pm => pm.supplierProductName && pm.internalProductId);
      const productsObject = {};
      filteredProducts.forEach(pm => {
        const supplierName = pm.supplierProductName.trim();
        if (supplierName) {
          productsObject[supplierName] = pm.internalProductName || '';
        }
      });

      const payload = {
        SupplierID: selectedSupplier.value,
        SupplierName: selectedSupplier.label,
        Mappings: mappings,
        Products: productsObject,
      };

      console.log('payload', payload);

      const response = await axios.post(`${BASE_URL}${POST_SUPPLIER_MAPPING}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200 && response.data?.isSucess) {
        toast.success(`Mapping ${isEditMode ? 'updated' : 'saved'} successfully`, {
          autoClose: 1500
        });
        setIsEditMode(true);

        // Update the supplier in the local suppliers array with the new mappings
        setSuppliers(prevSuppliers =>
          prevSuppliers.map(supplier => {
            const SupplierID = supplier.SupplierID || supplier.id;
            if (SupplierID === selectedSupplier.value) {
              return {
                ...supplier,
                Mappings: {
                  columns: mappings,
                  products: productsObject // Use the same object format as sent to API
                },
                Products: productsObject // Also set Products at root level for API compatibility
              };
            }
            return supplier;
          })
        );

        setExistingMapping(response.data?.data || payload);
      } else {
        throw new Error(response.data?.message || 'Failed to save mapping');
      }
    } catch (error) {
      console.error('Failed to save mapping:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        error.message ||
        'An error occurred while saving the mapping.';
      toast.error(errorMessage, { autoClose: 1500 });
    } finally {
      setLoading(false);
    }
  };

  // Handle product mapping change
  const handleProductMappingChange = (index, field, value, selectedOption = null) => {
    setProductMappings(prev => {
      const updated = [...prev];
      if (!updated[index]) {
        updated[index] = { supplierProductName: '', internalProductId: '', internalProductName: '' };
      }

      updated[index][field] = value;

      // If product is selected, update internal product name and store the option
      if (field === 'internalProductId') {
        if (value && selectedOption) {
          // Store the selected option for this index
          setSelectedProductOptions(prev => ({
            ...prev,
            [index]: selectedOption
          }));
          updated[index].internalProductName = selectedOption.label || selectedOption.Item_Name || selectedOption.ItemName || '';
        } else if (value) {
          // Try to find in productsMaster as fallback
          const product = productsMaster.find(p => p.id === value);
          if (product) {
            const option = {
              value: product.id,
              label: product.Item_Name ?? product.ItemName ?? '',
              ...product,
            };
            setSelectedProductOptions(prev => ({
              ...prev,
              [index]: option
            }));
            updated[index].internalProductName = product.Item_Name ?? product.ItemName ?? '';
          }
        } else {
          // Clear the stored option when product is cleared
          setSelectedProductOptions(prev => {
            const newOptions = { ...prev };
            delete newOptions[index];
            return newOptions;
          });
          updated[index].internalProductName = '';
        }
      }

      // Auto-add new row if both supplier name and product are filled
      // Only check when a value is being set (not cleared) and this is the last row
      if (value && value !== '' && index === updated.length - 1) {
        const currentMapping = updated[index];
        const hasSupplierName = currentMapping.supplierProductName &&
          currentMapping.supplierProductName.trim() !== '';
        const hasProductId = currentMapping.internalProductId &&
          currentMapping.internalProductId !== '';

        // If both fields are now filled, add a new empty row
        if (hasSupplierName && hasProductId) {
          // Check if there's already an empty row at the end (avoid duplicates)
          const lastRow = updated[updated.length - 1];
          const isLastRowComplete = lastRow.supplierProductName?.trim() && lastRow.internalProductId;

          if (isLastRowComplete) {
            // Add new empty row only if it doesn't already exist
            const hasEmptyRowAfter = updated.length > index + 1;
            if (!hasEmptyRowAfter) {
              updated.push({ supplierProductName: '', internalProductId: '', internalProductName: '' });
            }
          }
        }
      }

      return updated;
    });
  };

  // Add new product mapping
  const handleAddProductMapping = () => {
    setProductMappings(prev => [
      ...prev,
      { supplierProductName: '', internalProductId: '', internalProductName: '' }
    ]);
  };

  // Remove product mapping
  const handleRemoveProductMapping = (index) => {
    setProductMappings(prev => prev.filter((_, i) => i !== index));
    // Clean up stored options - reindex them
    setSelectedProductOptions(prev => {
      const newOptions = {};
      Object.keys(prev).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum < index) {
          newOptions[keyNum] = prev[key];
        } else if (keyNum > index) {
          newOptions[keyNum - 1] = prev[key];
        }
        // Skip the removed index
      });
      return newOptions;
    });
  };

  // Handle Excel file upload
  const handleExcelUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if Product Name column is mapped
    const productNameColumn = mappings.productName;
    if (!productNameColumn || productNameColumn.trim() === '') {
      toast.error('Please map the "Product Name" column first', { autoClose: 2000 });
      event.target.value = ''; // Reset file input
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        if (jsonData.length === 0) {
          toast.error('Excel file is empty', { autoClose: 2000 });
          event.target.value = '';
          return;
        }

        // Find the header row (usually first row)
        const headerRow = jsonData[0];
        const productNameColumnIndex = headerRow.findIndex(
          (header) => header && header.toString().trim().toLowerCase() === productNameColumn.trim().toLowerCase()
        );

        if (productNameColumnIndex === -1) {
          toast.error(`Column "${productNameColumn}" not found in Excel file`, { autoClose: 2000 });
          event.target.value = '';
          return;
        }

        // Extract product names from the mapped column (skip header row)
        const extractedProducts = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const productName = row[productNameColumnIndex];

          if (productName && productName.toString().trim() !== '') {
            const productNameStr = productName.toString().trim();
            // Avoid duplicates
            if (!extractedProducts.includes(productNameStr)) {
              extractedProducts.push(productNameStr);
            }
          }
        }

        if (extractedProducts.length === 0) {
          toast.error('No product names found in the mapped column', { autoClose: 2000 });
          event.target.value = '';
          return;
        }

        // Filter out products that are already mapped
        const mappedSupplierNames = productMappings
          .map(pm => pm.supplierProductName?.trim())
          .filter(name => name && name !== '');

        const unmatched = extractedProducts.filter(
          productName => !mappedSupplierNames.includes(productName)
        );

        setUnmatchedProducts(unmatched);

        if (unmatched.length === 0) {
          toast.success('All products from Excel are already mapped', { autoClose: 2000 });
        } else {
          toast.success(`Found ${unmatched.length} unmapped product(s) from Excel`, { autoClose: 2000 });
        }

        event.target.value = ''; // Reset file input
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        toast.error('Failed to parse Excel file. Please check the file format.', { autoClose: 2000 });
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      toast.error('Error reading file', { autoClose: 2000 });
      event.target.value = '';
    };

    reader.readAsArrayBuffer(file);
  };

  // Add unmatched product to mapping
  const handleAddUnmatchedProduct = (productName) => {
    // Check if already exists
    const exists = productMappings.some(
      pm => pm.supplierProductName?.trim() === productName.trim()
    );

    if (exists) {
      toast.warning('Product already in mapping list', { autoClose: 1500 });
      return;
    }

    // Add to product mappings
    const newIndex = productMappings.length;
    setProductMappings(prev => [
      ...prev,
      { supplierProductName: productName.trim(), internalProductId: '', internalProductName: '' }
    ]);
    // Clear any stored option for the new row
    setSelectedProductOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[newIndex];
      return newOptions;
    });

    // Remove from unmatched list
    setUnmatchedProducts(prev => prev.filter(p => p !== productName));

    toast.success('Product added to mapping', { autoClose: 1500 });
  };

  // Handle reset
  const handleReset = () => {
    setUnmatchedProducts([]); // Clear unmatched products on reset
    if (selectedSupplier) {
      loadExistingMapping(selectedSupplier.value);
    } else {
      setMappings(initializeMappings());
      setProductMappings([]);
      setSelectedProductOptions({});
    }
  };

  const supplierOptions = useMemo(
    () =>
      (suppliers || []).map((supplier) => {
        const SupplierID =
          supplier.SupplierID ??
          supplier.SupplierID ??
          supplier.Id ??
          supplier.id ??
          supplier.SupplierID;
        return {
          value: SupplierID,
          label: supplier.SupplierName ?? supplier.SupplierName ?? '',
          ...supplier,
        };
      }),
    [suppliers]
  );

  const customSupplierOptionLabel = (option) => {
    const SupplierName = option.SupplierName ?? option.SupplierName ?? option.label ?? 'Unknown supplier';
    return (
      <div className="flex items-center space-x-3 py-1">
        <div className="font-semibold text-sm text-black">
          {SupplierName}
        </div>
      </div>
    );
  };

  return (
    <div className='flex h-full bg-background'>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>
        <div className="bg-white p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                <FiFileText className="mr-2" />
                Supplier Excel Column Mapping
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Map Excel column names to purchase form fields for each supplier
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-black whitespace-nowrap">
                Supplier <span className="text-red-500">*</span>
              </label>
              <div className="w-80">
                <Select
                  inputId="supplier-input"
                  value={selectedSupplier}
                  options={supplierOptions}
                  formatOptionLabel={customSupplierOptionLabel}
                  onChange={handleSupplierChange}
                  isClearable
                  noOptionsMessage={() => "No suppliers found"}
                  styles={customSelectStyles}
                  className="w-full"
                  placeholder="Select a supplier..."
                />
              </div>
            </div>
          </div>
        </div>

        {selectedSupplier && (
          <div className="flex-1 flex overflow-hidden ml-3 mr-3 mb-3 gap-3">
            {/* Left Side - Column Mapping */}
            <div className="w-2/5 overflow-y-auto">
              <div className="bg-white rounded-lg shadow-sm p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
                  <h3 className="font-semibold text-black text-base">
                    Column
                  </h3>
                  <span className="text-xs text-indigo-600 font-medium">{selectedSupplier.label}</span>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="grid grid-cols-1 gap-3">
                    {PURCHASE_FIELDS.product.map((field) => (
                      <div key={field.key} className="flex items-center gap-3">
                        <label className="w-36 text-sm font-medium text-black flex items-center shrink-0">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                          <span className="text-gray-400 ml-2 text-xs">→</span>
                        </label>
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                          placeholder={`Excel column name`}
                          value={mappings[field.key] || ''}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Product Mapping */}
            <div className="w-full overflow-y-auto">
              <div className="bg-white rounded-lg shadow-sm p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
                  <h3 className="font-semibold text-black text-base">
                    Product Name Mapping
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Upload Excel button - only show if Product Name is mapped */}
                    {mappings.productName && mappings.productName.trim() !== '' && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleExcelUpload}
                          className="hidden"
                          id="excel-upload-input"
                        />
                        <label
                          htmlFor="excel-upload-input"
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <FiUpload size={14} />
                          Upload Excel
                        </label>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={handleAddProductMapping}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-1"
                    >
                      <FiPlus size={14} />
                      Add
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-4 -mt-2">
                  Map supplier product names to your internal products
                  {mappings.productName && mappings.productName.trim() !== '' && (
                    <span className="ml-2 text-green-600">• Product Name column mapped: "{mappings.productName}"</span>
                  )}
                </p>

                <div className="flex-1 overflow-y-auto">
                  {/* Unmatched Products from Excel */}
                  {unmatchedProducts.length > 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-yellow-800">
                          Unmapped Products from Excel ({unmatchedProducts.length})
                        </h4>
                        <button
                          type="button"
                          onClick={() => setUnmatchedProducts([])}
                          className="text-xs text-yellow-700 hover:text-yellow-900"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {unmatchedProducts.map((productName, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleAddUnmatchedProduct(productName)}
                            className="px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-xs font-medium rounded-md transition-colors border border-yellow-300"
                            title="Click to add to mapping"
                          >
                            {productName} <FiPlus size={12} className="inline ml-1" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {productMappings.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                      <FiFileText className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-gray-600">No product mappings yet</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {mappings.productName && mappings.productName.trim() !== ''
                          ? 'Upload Excel file or enter supplier name and select product to auto-add rows'
                          : 'Map "Product Name" column first, then upload Excel file or enter supplier name'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {productMappings.map((productMapping, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-black mb-1.5">
                              Supplier Product Name
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                              placeholder="Enter supplier product name"
                              value={productMapping.supplierProductName || ''}
                              onChange={(e) => handleProductMappingChange(index, 'supplierProductName', e.target.value)}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-black mb-1.5">
                              Internal Product
                            </label>
                            <AsyncSelect
                              loadOptions={loadProductOptions}
                              placeholder="Search product..."
                              isClearable
                              isSearchable
                              value={getSelectedProductOption(productMapping.internalProductId, selectedProductOptions[index])}
                              onChange={(selectedOption) => {
                                handleProductMappingChange(
                                  index, 
                                  'internalProductId', 
                                  selectedOption ? selectedOption.value : '',
                                  selectedOption
                                );
                              }}
                              onKeyDown={(e) => {
                                // Let react-select handle Enter key selection naturally
                                // Only prevent default if we need custom behavior
                                if (e.key === 'Enter') {
                                  const selectInstance = productSelectRefs.current[index];
                                  if (selectInstance) {
                                    // Check if menu is open - if not, allow default (form submission, etc.)
                                    const menuIsOpen = selectInstance.state?.menuIsOpen;
                                    if (!menuIsOpen) {
                                      // Menu is closed, allow default behavior
                                      return;
                                    }
                                    // Menu is open - let react-select handle it
                                    // Don't prevent default, let it select the focused option
                                  }
                                }
                              }}
                              ref={(el) => {
                                productSelectRefs.current[index] = el;
                              }}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue && inputValue.length < 2
                                  ? 'Type at least 2 characters'
                                  : 'No products found'
                              }
                              loadingMessage={() => 'Searching...'}
                              styles={customSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              defaultOptions={false}
                              cacheOptions={true}
                            />
                          </div>
                          <div className="flex items-end pt-6">
                            <button
                              type="button"
                              onClick={() => handleRemoveProductMapping(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove mapping"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Fixed at bottom */}
        {selectedSupplier && (
          <div className="bg-white ml-3 mr-3 mb-3 rounded-lg shadow-sm p-3 border-t border-gray-200">
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-700 font-medium text-sm rounded-md hover:bg-gray-300 transition-colors flex items-center gap-2"
                onClick={handleReset}
              >
                <FiX size={16} />
                Reset
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 transition-colors flex items-center gap-2"
                onClick={handleSave}
              >
                <FiSave size={16} />
                {isEditMode ? 'Update Mapping' : 'Save Mapping'}
              </button>
            </div>
          </div>
        )}

        {!selectedSupplier && (
          <div className="flex-1 flex items-center justify-center ml-3 mr-3 mb-3">
            <div className="text-center">
              <FiFileText className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-600 text-lg">Please select a supplier to configure column mappings</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuth(SupplierMapping);

